import * as React from "react";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";

import {Store} from "../../../project/reducer";
import {Dispatch} from "../../../../../shared/ts/interfaces/dispatch";
import Main from "../../../shared/layout/Main";
import Content from "../../../project/components/content/Content";
import ContentHeader from "../../../project/components/content/ContentHeader";
import ContentHeaderTitle from "../../../project/components/content/ContentHeaderTitle";
import ContentHeaderControls from "../../../project/components/content/ContentHeaderControls";
import {Button} from "../../../shared/button/Button";
import ContentBody from "../../../project/components/content/ContentBody";
import {FormActions} from "../../../../../shared/ts/helpers/create_form_actions";
import {closePanel, openPanel, openTab} from "../../../context_panel/actions/context_panel";
import {Offer} from "../../../shared/models/Offer";
import {fetchOfferDetailForNatives, resetOfferDetailForNative} from "../../../offer/actions/fetch";
import {noop} from "../../../../../shared/ts/helpers/lodash";
import {createFileName, urlBase64, urlToCroppedBase64} from "../../../../../shared/ts/helpers/urlToCroppedBase64";
import {url} from "../../../../../shared/ts/helpers/routing";
import {RouterProps} from "../../../../../shared/ts/interfaces/router";
import {RequestState} from "../../../../../shared/ts/helpers/util";
import {HocModalix, modalix} from "../../../../../shared/ts/decorators/modalix";
import {
    addSurpriseFormActions,
    surpriseAdsAdd,
    clearSurpriseAddEditViewState
} from "../../../advertisments/actions/surprise/add";
import {ISurpriseForms} from "../../../advertisments/reducers/surprise";
import {NativeOffersAutocomplete} from "../../../advertisments/components/NativeOffersAutocomplete";
import {htmlToImgDownload} from "../../native/actions/html_to_img";
import {saveAsHtml} from "../../native/actions/save_as_html";
import {ISurpriseAdCreation, SurpriseAdCreationForm} from "../components/SurpriseAdCreationForm";
import {SurpriseAdCreationAdModal} from "../components/SurpriseAdCreationAdModal";

interface IStateProps {
    advertDetail: ISurpriseForms;
    advertOfferDetail: Offer | null;
    contextPanelOpened: boolean;
}

interface IActionsProps {
    mobileFormActions: FormActions;
    fetchOfferDetailForNatives: (id: number) => Promise<any>;
    actions: {
        resetOfferDetailForNative: typeof resetOfferDetailForNative;
        clearSurpriseAddEditViewState: typeof clearSurpriseAddEditViewState;
        openPanel: typeof openPanel;
        closePanel: typeof closePanel;
        openTab: typeof openTab;
        surpriseAdsAdd: typeof surpriseAdsAdd;
    };
}

interface IOwnProps extends RouterProps {
    hoc: HocModalix;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

interface IState {
    initialImage: string | Blob;
}

@modalix(SurpriseAdCreationAdModal)
class SurpriseAdCreationAddViewC extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            initialImage: ""
        };
    }

    /**
     * Lifecycle
     */
    public componentDidMount() {
        this.getDefaultFormValues();
    }

    public componentWillUnmount(): void {
        this.props.actions.clearSurpriseAddEditViewState();
        this.props.actions.resetOfferDetailForNative();
    }

    private getDefaultFormValues = () => {
        const formValues = {
            mobile_offer_price_type: 0
        };

        this.props.mobileFormActions.update(formValues);
    };

    /**
     * Fetch offer and initialize form
     */

    private onSearchedOfferUpdate = async (data: {offer: {option: unknown; value: number} | null}): Promise<void> => {
        if (data.offer != null) {
            await this.props.fetchOfferDetailForNatives(data.offer.value);
            if (this.props.advertOfferDetail == null) {
                throw new Error("onSearchedOfferUpdate: offer not saved in store");
            }
            this.triggerPopulatingDataToForm(this.props.advertOfferDetail);
        }
    };

    private triggerPopulatingDataToForm = (offer: Offer): void => {
        const rawFormValues = this.prepareNativeCreationValues(offer);
        this.props.mobileFormActions.update(rawFormValues);
        this.prepareBase64Images(offer); // at the very end, because it is async anyway
    };

    // This supplements `prepareNativeCreationValues`.
    private prepareBase64Images = async (offer: Offer): Promise<void> => {
        const initialImage = await urlBase64(offer.main_image) as string;
        const mobileImage = await urlToCroppedBase64(offer.main_image, 375,  211);
        const logoImage = await urlToCroppedBase64(offer.logo, 80,  60);

        this.setState({initialImage});
        this.props.mobileFormActions.update({
            mobile_image: {
                ...this.props.advertDetail.form.image,
                selected: `main_${createFileName(offer.name, offer.pk)}`,
                base64value: mobileImage
            },
            logo: {
                ...this.props.advertDetail.form.image,
                selected: `logo_${createFileName(offer.name, offer.pk)}`,
                base64value: logoImage
            }
        });
    };

    private prepareNativeCreationValues = (offer: Offer) => {
        return {
            mobile_name: offer.name,
            mobile_address: offer.street_name + (offer.street_number ? ` ${offer.street_number}` : ""),
            mobile_region: offer.region.short_name,
            mobile_vendor: offer.vendor.name
        };
    };

    /**
     * Submit form
     */
    private onSubmit = (shouldGenerate: boolean = false, toHtml: boolean = false) => {

        const creation: ISurpriseAdCreation = {
            ...SurpriseAdCreationForm.toJSON(this.props.advertDetail.form),
        };
        // ToDO: just for now, later add new reducer to store form data and separate
        const creationUpdated = {...creation}
        this.handleFormSubmit(creationUpdated, shouldGenerate, toHtml);
    };

    private handleFormSubmit = (creation: ISurpriseAdCreation, shouldGenerate: boolean, toHtml: boolean) => {
        (this.props.actions.surpriseAdsAdd(creation as any) as any) // ANY: actions' types are based on old creation types
            .then((response: ISurpriseAdCreation) => {
                if (response != null) {
                    if (shouldGenerate) {
                        if (toHtml) {
                            this.handleSaveHtml(response.name, response.id);
                        } else {
                            this.handleConvert(response.name, response.id);
                        }
                    }
                    return response.id;
                }
            })
            .then((id: number) => {
                if (id != null) {
                    const link = url("app:in:creation:image:edit", {id});
                    this.props.router.push(link);
                }
            });
    };

    private handleConvert = (name: string, id: number) => {
        this.openPreviewModal();
        htmlToImgDownload(document.getElementById("v2-ad")!, name, id);

        // do we want to wait?
        window.setTimeout(() => {
            this.props.hoc.modalix.hide();
        }, 3000);
    };

    private handleSaveHtml = (name: string, id: number) => {
        this.openPreviewModal();
        const element = document.getElementById("v2-ad")!;
        saveAsHtml(element, name, id);
        window.setTimeout(() => {
            this.props.hoc.modalix.hide();
        }, 3000);
    };

    /**
     * Callback
     */

    private openPreviewModal = () => {
        this.props.hoc.modalix.show<any>({
            imageAdCreationFormData: this.props.advertDetail.form,
            onReject: () => this.props.hoc.modalix.hide()
        });
    };

    private onImageCropped = (key: ("image"), image?: string) => {
        const imgOptions = image ?
            {base64value: image} :
            {base64value: this.state.initialImage, selected: this.props.advertOfferDetail ? createFileName(this.props.advertOfferDetail.name, this.props.advertOfferDetail.pk) : "initial_image"};
        this.props.mobileFormActions.update({
            [key]: {
                ...this.props.advertDetail.form[key],
                ...imgOptions
            }
        });
    };

    /**
     * Render
     */

    private emptyAutocompleteFormValues = {}; // autocomplete does not need `value` entry

    public render() {
        const {request} = this.props.advertDetail;
        return (
            <Main>
                <Content>
                    <ContentHeader>
                        <ContentHeaderTitle>Reklamy natywne</ContentHeaderTitle>

                        <ContentHeaderControls>
                            <Button
                                className="ttu ph-xxl mr-xl"
                                type="submit"
                                color="primary-ghost"
                                size="sm"
                                onClick={() => this.openPreviewModal()}
                            >
                                Podgląd Reklam
                            </Button>

                            <Button
                                className="ttu ph-xxl mr-xl"
                                type="submit"
                                color="success"
                                size="sm"
                                submitting={request === RequestState.Waiting}
                                onClick={() => this.onSubmit()}
                            >
                                Zapisz Reklamy
                            </Button>

                            <Button
                                className="ttu ph-xxl"
                                type="submit"
                                color="primary"
                                size="sm"
                                submitting={request === RequestState.Waiting}
                                onClick={() => this.onSubmit(true)}
                            >
                                Zapisz i Wygeneruj Reklamy
                            </Button>
                        </ContentHeaderControls>
                    </ContentHeader>

                    <ContentBody>
                        <NativeOffersAutocomplete
                            onSubmit={noop}
                            errors={null}
                            values={this.emptyAutocompleteFormValues}
                            updateFormAction={this.onSearchedOfferUpdate}
                        />

                        <SurpriseAdCreationForm
                            onSubmit={() => this.onSubmit(false)}
                            errors={this.props.advertDetail.errors ? this.props.advertDetail.errors.fieldErrors : null}
                            values={this.props.advertDetail.form}
                            updateFormAction={this.props.mobileFormActions.update}
                            handlePreview={() => this.openPreviewModal()}
                            submitAndGenerate={() => this.onSubmit(true)}
                            submitAndGenerateHtml={() => this.onSubmit(true, true)}
                            updateImageAfterCropping={this.onImageCropped}
                            initialImage={this.state.initialImage}
                        />
                    </ContentBody>
                </Content>
            </Main>
        );
    }
}

export const SurpriseAdCreationAddView = connect(mapStateToProps, mapActionsToProps)(SurpriseAdCreationAddViewC);


/**
 * Connect
 */

function mapStateToProps(store: Store): IStateProps {
    return {
        advertDetail: store.adverts.surprise.detail,
        advertOfferDetail: store.adverts.surprise.nativesOfferDetail,
        contextPanelOpened: store.adverts.surprise.list.contextPanel.opened
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        mobileFormActions: bindActionCreators(addSurpriseFormActions, dispatch),
        fetchOfferDetailForNatives: fetchOfferDetailForNatives(dispatch),
        actions: bindActionCreators({
            surpriseAdsAdd,
            openPanel,
            closePanel,
            openTab,
            clearSurpriseAddEditViewState,
            resetOfferDetailForNative
        }, dispatch)
    };
}

